import React from "react";
import { Link } from "react-router-dom";
import { Scale, ShieldAlert, Landmark, Mail, ArrowRight, GitBranch, Check, Circle } from "lucide-react";

/**
 * MAVULA Investment Ltd — Legal Status & Regulatory Position
 * Public disclosure page. Kept deliberately conservative and factual.
 */
const LAST_UPDATED = "5 August 2026";
const CRN = "17368822";
const OFFICE = "Lewis Building, 35 Bull Street, Birmingham, B4 6AF, United Kingdom";

export default function LegalStatusPage() {
  return (
    <main data-testid="legal-status-page" className="bg-white">
      {/* Hero */}
      <section className="mv-green-bg text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Legal Information</div>
          <h1 className="font-display text-3xl md:text-5xl mt-4 leading-[1.02]">
            Legal Information & <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Regulatory Status</span>
          </h1>
          <p className="mt-4 text-white/80 text-[13px]">
            Last Updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 space-y-14">
          {/* Company Registration */}
          <div>
            <div className="flex items-center gap-3">
              <Landmark size={22} className="mv-green-text"/>
              <h2 className="font-display text-2xl md:text-3xl mv-green-text">Company Registration</h2>
            </div>
            <div className="mt-4 mv-card p-6 space-y-2 text-[14.5px]" style={{ color: "var(--mv-ink)" }} data-testid="legal-company-registration">
              <div><strong>Mavula Investment Ltd</strong></div>
              <div>Registered in England &amp; Wales, United Kingdom</div>
              <div>Companies House Registration Number: <strong>{CRN}</strong></div>
              <div>Registered Office: {OFFICE}</div>
              <div className="pt-2 text-[13.5px]" style={{ color: "var(--mv-muted)" }}>Botswana registered operations in progress.</div>
            </div>
          </div>

          {/* Regulatory Position */}
          <div>
            <div className="flex items-center gap-3">
              <Scale size={22} className="mv-green-text"/>
              <h2 className="font-display text-2xl md:text-3xl mv-green-text">Current Regulatory Position</h2>
            </div>
            <div className="mt-4 space-y-4 text-[14.5px] leading-relaxed" style={{ color: "var(--mv-ink)" }} data-testid="legal-regulatory-position">
              <p>
                Mavula Investment Ltd is <strong>not currently authorised or regulated by the UK Financial Conduct Authority (FCA)</strong>. We do not offer regulated banking, investment, lending, or financial advice services at this time.
              </p>
              <p>What we provide today falls under applicable permitted activities:</p>
              <ul className="space-y-2 pl-5 list-disc">
                <li>Introduction and facilitation of property purchase opportunities</li>
                <li>Administration of structured payment plans toward asset ownership</li>
                <li>Secure processing of payments through licensed third-party providers including Stripe and Flutterwave</li>
              </ul>
              <p>
                We are actively working toward obtaining relevant regulatory authorisations including <strong>FCA Payment Institution status</strong> and applicable approvals in Southern African jurisdictions. Any future regulated services will only be launched once all permissions are fully granted.
              </p>
            </div>
          </div>

          {/* Important Statements */}
          <div>
            <div className="flex items-center gap-3">
              <ShieldAlert size={22} style={{ color: "#8a2020" }}/>
              <h2 className="font-display text-2xl md:text-3xl mv-green-text">Important Statements</h2>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="legal-important-statements">
              {[
                { t: "No guaranteed returns", d: "We do not offer, market, or promise fixed interest, yields, dividends, or investment profits." },
                { t: "Not a bank", d: "We do not hold deposits in the capacity of a financial institution; funds are processed and held securely by our licensed payment partners." },
                { t: "No financial advice", d: "We do not provide legal, tax, or financial guidance. All users are strongly encouraged to seek independent professional advice before entering any property transaction." },
                { t: "Fair terms subject to checks", d: "All purchase pathways are subject to successful property valuation, independent legal review, and compliance with anti-money-laundering and verification requirements." },
              ].map((c) => (
                <div key={c.t} className="mv-card p-5" style={{ background: "rgba(212,60,60,0.04)", borderColor: "rgba(212,60,60,0.15)" }}>
                  <div className="font-display text-[16px] mv-green-text">{c.t}</div>
                  <p className="mt-1 text-[13.5px]" style={{ color: "var(--mv-ink)" }}>{c.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Partners */}
          <div>
            <h2 className="font-display text-2xl md:text-3xl mv-green-text">Payment Partners</h2>
            <p className="mt-3 text-[14.5px] leading-relaxed" style={{ color: "var(--mv-ink)" }}>
              All monetary transactions are processed through fully regulated service providers. Their terms, privacy policies, and regulatory status apply alongside ours.
            </p>
          </div>

          {/* Path to Full Regulation — phased plan */}
          <div data-testid="legal-regulation-roadmap">
            <div className="flex items-center gap-3">
              <GitBranch size={22} className="mv-green-text"/>
              <h2 className="font-display text-2xl md:text-3xl mv-green-text">Our Path to Full Regulation</h2>
            </div>
            <p className="mt-3 text-[14.5px] leading-relaxed" style={{ color: "var(--mv-ink)" }}>
              We are open about where we are today and where we are going. Below is our honest, phased plan — designed so we can safely start helping citizens now, while working toward the highest UK regulatory standards. Nothing on this page is a promise of timing or outcome.
            </p>

            <ol className="mt-6 space-y-4">
              {[
                {
                  status: "current",
                  chip: "Now — active",
                  title: "Phase 1 · Property Introducer (unregulated activity)",
                  points: [
                    "Introduce buyers to verified Southern African developer projects",
                    "All funds handled by licensed payment partners (Stripe, Flutterwave) — Mavula never holds client money",
                    "HMRC AML supervision as an Estate Agency Business (registration in progress)",
                    "Voluntary Property Ombudsman membership planned before first sale",
                  ],
                },
                {
                  status: "next",
                  chip: "Next 3–4 months",
                  title: "Phase 2 · Small Payment Institution (SPI) registration",
                  points: [
                    "File Small Payment Institution registration with the FCA",
                    "Allows Mavula to hold and move up to €3m/month directly",
                    "Independent legal opinion on the introducer model in the meantime",
                    "Real customer pilots begin in Botswana once local escrow attorney is confirmed",
                  ],
                },
                {
                  status: "future",
                  chip: "Later — when volume justifies",
                  title: "Phase 3 · Full Payment Institution (PI) authorisation",
                  points: [
                    "Full FCA Payment Institution authorisation",
                    "Direct settlement of large project cashflows through Mavula-controlled safeguarded accounts",
                    "Southern African payment institution / EMD licences where locally applicable",
                  ],
                },
              ].map((p) => (
                <li key={p.title} className="mv-card p-5 md:p-6" style={{ background: p.status === "current" ? "linear-gradient(180deg, #fff, rgba(10,59,40,0.05))" : "#fff" }}>
                  <div className="flex flex-wrap items-center gap-3">
                    {p.status === "current"
                      ? <Check size={18} className="mv-green-text"/>
                      : <Circle size={16} style={{ color: "var(--mv-muted)" }}/>}
                    <span className="text-[10.5px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full font-semibold"
                          style={{ background: p.status === "current" ? "rgba(10,59,40,0.10)" : "rgba(0,0,0,0.04)",
                                   color: p.status === "current" ? "var(--mv-green)" : "var(--mv-muted)" }}>
                      {p.chip}
                    </span>
                  </div>
                  <div className="mt-3 font-display text-[17px] mv-green-text">{p.title}</div>
                  <ul className="mt-3 space-y-1.5 text-[13.5px]" style={{ color: "var(--mv-ink)" }}>
                    {p.points.map((pt) => (
                      <li key={pt} className="flex gap-2 items-start">
                        <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: "var(--mv-gold)" }}/>
                        {pt}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>

            <div className="mt-5 text-[12px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
              Timelines are indicative. Regulatory processes depend on the FCA and local supervisors and may take longer. Nothing here creates a legal obligation.
            </div>
          </div>

          {/* Contact */}
          <div>
            <div className="flex items-center gap-3">
              <Mail size={22} className="mv-green-text"/>
              <h2 className="font-display text-2xl md:text-3xl mv-green-text">Contact For Queries</h2>
            </div>
            <div className="mt-4 mv-card p-6 space-y-2 text-[14.5px]" style={{ color: "var(--mv-ink)" }} data-testid="legal-contact">
              <div><strong>All queries:</strong> <a href="mailto:Sebata@pulalove.com" className="underline mv-green-text">Sebata@pulalove.com</a></div>
              <div className="pt-2 text-[13px]" style={{ color: "var(--mv-muted)" }}>
                Please mark your subject line "Legal &amp; Compliance" or "General". Domain <span className="font-mono">mavulainvestment.com</span> is being verified — until then, all correspondence goes to the address above.
              </div>
            </div>
          </div>

          {/* Return to About */}
          <div className="pt-4">
            <Link to="/about" className="btn-ghost" data-testid="legal-back-about">
              Back to About Us <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
